/**
 * TP / FC 充值记录 — Node `user_recharge` 分页列表（独立页，不挂数据看板）。
 */
import { useState } from 'react';
import { Button, Input, Modal, Select, Space, Tag, Typography, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

type OnchainAsset = 'tp' | 'fc';

interface OnchainRechargeRow {
  id: number;
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string;
  asset: OnchainAsset;
  method: string;
  amount: string;
  payment_usdt: string | null;
  from_address: string | null;
  tx_hash: string | null;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected' | string;
  create_time: string;
}

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  pending: { color: 'default', text: '待提交' },
  reviewing: { color: 'orange', text: '审核中' },
  approved: { color: 'green', text: '已通过' },
  rejected: { color: 'red', text: '已拒绝' },
};

export function OnchainRechargeRecordsPage({ asset }: { asset: OnchainAsset }) {
  const title = asset === 'tp' ? 'TP充值' : 'FC充值';
  const amountLabel = asset === 'tp' ? 'TP 金额' : 'FC 金额';
  const endpoint = asset === 'tp' ? '/api/admin/tp-recharge/records' : '/api/admin/fc-recharge/records';
  const [status, setStatus] = useState<string | undefined>();
  const [username, setUsername] = useState('');
  const [appliedUsername, setAppliedUsername] = useState('');
  const [rejectTarget, setRejectTarget] = useState<OnchainRechargeRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const hasNode = useAuthStore((s) => s.hasNode);
  const qc = useQueryClient();

  const canApprove = hasNode('platform.recharge.approve');
  const canReject = hasNode('platform.recharge.reject');

  async function approve(row: OnchainRechargeRow) {
    try {
      await api.post(`/api/admin/recharge/${row.id}/approve`);
      message.success(`已通过充值 #${row.id}`);
      qc.invalidateQueries({ queryKey: ['admin', `${asset}-recharge`] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '审核通过失败');
    }
  }

  async function reject() {
    if (!rejectTarget) return;
    try {
      await api.post(`/api/admin/recharge/${rejectTarget.id}/reject`, { reason: rejectReason });
      message.success(`已拒绝充值 #${rejectTarget.id}`);
      setRejectTarget(null);
      setRejectReason('');
      qc.invalidateQueries({ queryKey: ['admin', `${asset}-recharge`] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '拒绝失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户编号', dataIndex: 'player_number', width: 120 },
    { title: '用户名', dataIndex: 'username', width: 140 },
    { title: amountLabel, dataIndex: 'amount', width: 120 },
    { title: 'USDT', dataIndex: 'payment_usdt', width: 120, render: (v: string | null) => v ?? '—' },
    { title: '渠道', dataIndex: 'method', width: 120 },
    {
      title: '交易哈希',
      dataIndex: 'tx_hash',
      ellipsis: true,
      render: (v: string | null) =>
        v ? (
          <Typography.Text copyable ellipsis style={{ maxWidth: 220 }}>
            {v}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (v: string) => {
        const t = STATUS_TAG[v] ?? { color: 'default', text: v };
        return <Tag color={t.color}>{t.text}</Tag>;
      },
    },
    { title: '提交时间', dataIndex: 'create_time', width: 180 },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: unknown, row: OnchainRechargeRow) =>
        row.status === 'reviewing' ? (
          <Space>
            {canApprove && (
              <Button size="small" type="primary" onClick={() => approve(row)}>
                通过
              </Button>
            )}
            {canReject && (
              <Button size="small" danger onClick={() => setRejectTarget(row)}>
                拒绝
              </Button>
            )}
          </Space>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          placeholder="用户名"
          value={username}
          style={{ width: 180 }}
          onChange={(e) => setUsername(e.target.value)}
          onPressEnter={() => setAppliedUsername(username.trim())}
        />
        <Button onClick={() => setAppliedUsername(username.trim())}>查询</Button>
        <Select
          value={status}
          style={{ width: 160 }}
          onChange={setStatus}
          allowClear
          placeholder="状态"
          options={[
            { value: 'pending', label: '待提交' },
            { value: 'reviewing', label: '审核中' },
            { value: 'approved', label: '已通过' },
            { value: 'rejected', label: '已拒绝' },
          ]}
        />
      </Space>

      <DataTable<OnchainRechargeRow>
        queryKey={['admin', `${asset}-recharge`, status ?? 'all', appliedUsername]}
        endpoint={endpoint}
        extraParams={{
          ...(status ? { status } : {}),
          ...(appliedUsername ? { username: appliedUsername } : {}),
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1400 }}
      />

      <Modal
        open={rejectTarget !== null}
        title={`拒绝充值 #${rejectTarget?.id}`}
        onOk={reject}
        onCancel={() => {
          setRejectTarget(null);
          setRejectReason('');
        }}
        okText="确认拒绝"
        okButtonProps={{ danger: true, disabled: !rejectReason }}
      >
        <Input.TextArea
          rows={3}
          placeholder="拒绝原因（用户可见）"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}

export function TpRechargeRecordsPage() {
  return <OnchainRechargeRecordsPage asset="tp" />;
}

export function FcRechargeRecordsPage() {
  return <OnchainRechargeRecordsPage asset="fc" />;
}
